import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { HighlightEditor } from '@/components/ui/highlight-editor';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { HighlightableSelect } from '@/components/ui/highlightable-select';
import { QuickSelectInput } from '@/components/ui/quick-select-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Plus, Trash2 } from 'lucide-react';
import { useState, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { ResponsiveImageUpload } from '../shared/responsive-image-upload';
import { uploadSiteSurveyPhoto } from '../shared/site-survey-photo-upload';

interface AcInstallationProps {
  form: UseFormReturn<any>;
  installationId?: number;
  surveyId?: number;
  onPhotoUploaded?: () => void;
}

export function AcInstallation({
  form,
  installationId,
  surveyId,
  onPhotoUploaded,
}: AcInstallationProps) {
  const { toast } = useToast();
  const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});
  const fileInputRefs = {
    cableRouteImage1: useRef<HTMLInputElement>(null),
    cableRouteImage2: useRef<HTMLInputElement>(null),
  };

  const inverterCircuits = form.watch('inverterCircuits') || [];

  const addAcCircuit = () => {
    const currentCircuits = form.getValues('inverterCircuits') || [];
    const newCircuit = {
      id: Date.now().toString(),
      circuitPurpose: '',
      type: '',
      cca: '',
      length: '',
      faultProtection: '',
      deviceType: '',
      notes: '',
    };
    form.setValue('inverterCircuits', [...currentCircuits, newCircuit]);
  };

  const removeAcCircuit = (index: number) => {
    const currentCircuits = form.getValues('inverterCircuits') || [];
    form.setValue(
      'inverterCircuits',
      currentCircuits.filter((_: any, i: number) => i !== index)
    );
  };

  const getCircuitPurposeLabel = (purpose: string) => {
    switch (purpose) {
      case 'INVERTER': return 'Inverter';
      case 'EV': return 'EV';
      case 'OTHER': return 'Other';
      default: return 'AC';
    }
  };

  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  const handleImageUpload = async (field: string, file: File) => {
    setUploadingImages((prev) => ({ ...prev, [field]: true }));

    try {
      await uploadSiteSurveyPhoto({
        form,
        field,
        file,
        surveyId,
        installationId,
        customerId: form.getValues('customerId'),
        description: `AC Installation ${field} photo`,
        toast,
        onPhotoUploaded,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description:
          error instanceof Error ? error.message : 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImages((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFileSelect = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(field, file);
    }
    event.target.value = '';
  };

  const handleCameraCapture = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      if (isMobileDevice()) {
        input.current.setAttribute('capture', 'environment');
      } else {
        input.current.removeAttribute('capture');
      }
      input.current.click();
    }
  };

  const triggerFileUpload = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      input.current.removeAttribute('capture');
      input.current.click();
    }
  };

  const renderImageUpload = (fieldName: string, label: string) => {
    return (
      <ResponsiveImageUpload
        form={form}
        fieldName={fieldName}
        label={label}
        isUploading={uploadingImages[fieldName]}
        onCameraCapture={() => handleCameraCapture(fieldName)}
        onFileUpload={() => triggerFileUpload(fieldName)}
        onPhotoDeleted={onPhotoUploaded}
      />
    );
  };

  return (
    <div className="space-y-6">
      {Object.entries(fileInputRefs).map(([key, ref]) => (
        <input
          key={key}
          ref={ref}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={handleFileSelect(key)}
        />
      ))}

      <Card>
        <CardHeader>
          <CardTitle>AC Installation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="inverterDb"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inverter DB</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select inverter DB type"
                      options={[
                        { value: 'INDOOR', label: 'Indoor' },
                        { value: 'IP65', label: 'IP65' },
                        { value: 'GATEWAY', label: 'Gateway' },
                        { value: 'USE_EXISTING', label: 'Use Existing' },
                      ]}
                      fieldName="inverterDb"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="acSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Size</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select size"
                      options={[
                        { value: 'STANDARD', label: 'Standard' },
                        { value: 'ADDITIONAL_WAYS', label: 'Additional Ways' },
                      ]}
                      fieldName="acSize"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="switchFuseRequired"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Switch Fuse Required</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select option"
                      options={[
                        { value: 'NO', label: 'No' },
                        { value: 'REQUIRED', label: 'Required (for dist circuit or tails >3m)' },
                      ]}
                      fieldName="switchFuseRequired"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="exportLimitation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Export Limitation</FormLabel>
                  <FormControl>
                    <QuickSelectInput
                      value={field.value || ''}
                      onChange={field.onChange}
                      presetValues={[
                        { value: '3.6', label: '3.6 kW' },
                        { value: '4', label: '4 kW' },
                        { value: '5', label: '5 kW' },
                        { value: '6', label: '6 kW' },
                        { value: '7', label: '7 kW' },
                        { value: '10', label: '10 kW' },
                      ]}
                      placeholder="Enter value"
                      unit="kW"
                      inputType="number"
                      fieldName="exportLimitation"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {inverterCircuits.length === 0 && (
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">AC Circuits</h3>
            <Button type="button" variant="outline" size="sm" onClick={addAcCircuit} data-testid="button-add-ac-circuit">
              <Plus className="w-4 h-4 mr-2" />
              Add AC Circuit
            </Button>
          </div>
        )}

        {inverterCircuits.map((circuit: any, index: number) => (
          <Card key={circuit.id || index}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {getCircuitPurposeLabel(circuit.circuitPurpose)} Circuit {index + 1} (DB to ISO)
                </CardTitle>
                {inverterCircuits.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAcCircuit(index)}
                    data-testid={`button-remove-ac-circuit-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name={`inverterCircuits.${index}.circuitPurpose`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Circuit Purpose</FormLabel>
                    <FormControl>
                      <HighlightableSelect
                        value={field.value || ''}
                        onValueChange={field.onChange}
                        placeholder="Select circuit purpose"
                        options={[
                          { value: 'INVERTER', label: 'Inverter Circuit' },
                          { value: 'EV', label: 'EV Circuit' },
                          { value: 'OTHER', label: 'Other' },
                        ]}
                        fieldName={`inverterCircuits.${index}.circuitPurpose`}
                        form={form}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={`inverterCircuits.${index}.type`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <HighlightableSelect
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          placeholder="Select cable type"
                          options={[
                            { value: 'SWA', label: 'SWA' },
                            { value: 'EVULTRA', label: 'EVULTRA' },
                            { value: 'H07', label: 'H07' },
                            { value: 'T&E', label: 'T&E' },
                            { value: 'OTHER', label: 'Other' },
                          ]}
                          fieldName={`inverterCircuits.${index}.type`}
                          form={form}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`inverterCircuits.${index}.cca`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CCA</FormLabel>
                      <FormControl>
                        <HighlightableSelect
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          placeholder="Select CCA"
                          options={[
                            { value: '4MM', label: '4mm' },
                            { value: '6MM', label: '6mm' },
                            { value: '10MM', label: '10mm' },
                            { value: '16MM', label: '16mm' },
                            { value: 'OTHER', label: 'Other' },
                            { value: 'TBC', label: 'TBC' },
                          ]}
                          fieldName={`inverterCircuits.${index}.cca`}
                          form={form}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={`inverterCircuits.${index}.length`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Length</FormLabel>
                      <FormControl>
                        <HighlightableSelect
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          placeholder="Select length"
                          options={Array.from({ length: 50 }, (_, i) => i + 1).map((num) => ({
                            value: num.toString(),
                            label: num.toString(),
                          }))}
                          fieldName={`inverterCircuits.${index}.length`}
                          form={form}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`inverterCircuits.${index}.faultProtection`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>AC Circuit Fault Protection</FormLabel>
                      <FormControl>
                        <HighlightableSelect
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          placeholder="Select protection"
                          options={[
                            { value: '20', label: '20' },
                            { value: '32', label: '32' },
                            { value: '40', label: '40' },
                            { value: '50', label: '50' },
                            { value: '63', label: '63' },
                            { value: '80', label: '80' },
                            { value: '100', label: '100' },
                            { value: '125', label: '125' },
                            { value: 'TBC', label: 'TBC' },
                          ]}
                          fieldName={`inverterCircuits.${index}.faultProtection`}
                          form={form}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={`inverterCircuits.${index}.deviceType`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Device Type</FormLabel>
                    <FormControl>
                      <HighlightableSelect
                        value={field.value || ''}
                        onValueChange={field.onChange}
                        placeholder="Select device type"
                        options={[
                          { value: 'MCB', label: 'MCB' },
                          { value: 'RCBO', label: 'RCBO' },
                          { value: 'OTHER', label: 'Other' },
                        ]}
                        fieldName={`inverterCircuits.${index}.deviceType`}
                        form={form}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`inverterCircuits.${index}.notes`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <HighlightEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Additional notes for this circuit..."
                        savedStatus={!!surveyId}
                        minHeight="100px"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        ))}

        {inverterCircuits.length > 0 && (
          <Button type="button" variant="outline" onClick={addAcCircuit} className="w-full" data-testid="button-add-additional-ac-circuit">
            <Plus className="w-4 h-4 mr-2" />
            Add Additional AC Circuit
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cable Route Images</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderImageUpload('cableRouteImage1', 'Cable Route Image 1')}
            {renderImageUpload('cableRouteImage2', 'Cable Route Image 2')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
